import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SessionTextChatViewProps} from 'components';
import {DynamicComponentNames} from 'constants/client';
import {VoiceInputMode} from 'enums';
import {AnyParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {FullScreenTextChat, ClientSessionTextChatView} from 'pages/Client';
import {TextChatContentType, TextChatMessage, UserParticipantRole} from 'types';
import {generateUtils} from 'utils';

import {whiteLabelPreviewEvents} from '../WhiteLabelPreviewEvents';

const AGENT_ID = '1';
const CLIENT_ID = '2';
const ASSISTANT_ID = '3';
const SYSTEM_ID = 'system';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL.PREVIEW_MODAL;
const T_ELEMENT_CLICKED = T_KEYS.EVENTS.ELEMENT_CLICKED;
const T_COMMON_LABELS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

const getSessionTextChatViewProps: (TFunction) => SessionTextChatViewProps = (t) => {
    const elementClicked = () => {
        whiteLabelPreviewEvents.mock(t(T_ELEMENT_CLICKED));
    };
    const agent = {
        id: AGENT_ID,
        name: t(T_COMMON_LABELS.AGENT)
    };
    const client = {
        id: CLIENT_ID,
        name: t(T_COMMON_LABELS.CLIENT)
    };
    const assistant = {
        id: ASSISTANT_ID,
        name: t(T_COMMON_LABELS.ASSISTANT)
    };
    const system = {
        id: SYSTEM_ID,
        name: t(T_COMMON_LABELS.SYSTEM)
    };
    return {
        message: '',
        participants: [agent, client, assistant, system] as any as AnyParticipant[],
        messages: [
            {
                id: generateUtils.generateToken(),
                date: new Date().toISOString(),
                content: t(T_KEYS.SAMPLE_CHAT.MESSAGE_1),
                contentType: TextChatContentType.Text,
                from: agent
            },
            {
                id: generateUtils.generateToken(),
                date: new Date().toISOString(),
                content: t(T_KEYS.SAMPLE_CHAT.MESSAGE_2),
                contentType: TextChatContentType.Text,
                from: client
            },
            {
                id: generateUtils.generateToken(),
                date: new Date().toISOString(),
                content: t(T_KEYS.SAMPLE_CHAT.MESSAGE_3),
                contentType: TextChatContentType.Text,
                from: agent
            },
            {
                id: generateUtils.generateToken(),
                date: new Date().toISOString(),
                content: t(T_KEYS.SAMPLE_CHAT.MESSAGE_4),
                contentType: TextChatContentType.Text,
                from: assistant
            },
            {
                id: generateUtils.generateToken(),
                date: new Date().toISOString(),
                content: t(T_KEYS.SAMPLE_CHAT.INTERACTIVE_CHAT.LABEL),
                contentType: TextChatContentType.InteractiveChat,
                data: {
                    options: [
                        {content: t(T_KEYS.SAMPLE_CHAT.INTERACTIVE_CHAT.OPTION_1), contentType: TextChatContentType.InteractiveChatAnswer},
                        {content: t(T_KEYS.SAMPLE_CHAT.INTERACTIVE_CHAT.OPTION_2), contentType: TextChatContentType.InteractiveChatAnswer}
                    ]
                },
                from: system
            },
        ],
        fileIsUploading: false,
        downloadingFiles: [],
        typing: false,
        localParticipantId: client.id,
        typingRemoteParticipantIds: [],
        userParticipantIds: {
            [UserParticipantRole.Agent]: [AGENT_ID],
            [UserParticipantRole.Assistant]: [ASSISTANT_ID]
        },
        submitDisabled: true,
        downloadFileDisabled: false,
        optionSelectInProgress: false,
        messagesEndRef: null,
        onSubmit: elementClicked,
        downloadFile: elementClicked,
        handleUploadFile: elementClicked,
        openFileFileInput: elementClicked,
        setSendFileInputRef: whiteLabelPreviewEvents.empty,
        getTranslation: (msg: Partial<TextChatMessage>) => msg.content,
        onChange: null
    };
};

type Props = {
    componentName: DynamicComponentNames;
};

export const TextChatPreview: React.FC<Props> = ({componentName}) => {
    const {t} = useTranslation();
    const [message, setMessage] = useState('');
    const [voiceRecording, setVoiceRecording] = useState(false);
    const sessionTextChatViewProps = useMemo(() => getSessionTextChatViewProps(t), [t]);

    // The preview shows the built-in hold-to-talk behaviour; the account's own mode only applies in a session.
    const voiceInput = useMemo(() => ({
        enabled: true,
        recording: voiceRecording,
        mode: VoiceInputMode.PushToTalk,
        onStart: () => setVoiceRecording(true),
        onStop: () => setVoiceRecording(false)
    }), [voiceRecording]);

    const renderTextChat = useCallback(() => (
        <ClientSessionTextChatView
            {...sessionTextChatViewProps}
            message={message}
            voiceInput={voiceInput}
            onChange={setMessage}
        />
    ), [sessionTextChatViewProps, message, voiceInput, setMessage]);

    return (
        <Fragment>
            {(componentName === DynamicComponentNames.TextChat) && renderTextChat()}
            {(componentName === DynamicComponentNames.FullScreenTextChat) &&
                <FullScreenTextChat TextChatComponent={renderTextChat} />}
        </Fragment>
    );
};
